<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import CurrencyText from '@uiKit/components/CurrencyText.vue'

// Tinted note block on the sell steps (Figma "Note"): amber lock warning,
// red band / rejection notes, blue review note, grey tips.

export type SellNoteTone = 'warning' | 'danger' | 'info' | 'neutral'

interface Props {
  text: string
  tone?: SellNoteTone
  title?: string
  icon?: PiIconName
}

const props = withDefaults(defineProps<Props>(), { tone: 'neutral' })

const TONE_COLOR: Record<SellNoteTone, string> = {
  warning: 'var(--p-warning-600)',
  danger: 'var(--p-danger-600)',
  info: 'var(--p-info-700)',
  neutral: 'var(--p-surface-500)',
}

const color = computed(() => TONE_COLOR[props.tone])
</script>

<template>
  <Box align="start" gap="10px" :class="['sell-note', `sell-note--${tone}`]">
    <PiIcon v-if="icon" :name="icon" class="sell-note__icon" :style="{ color }" />
    <Box direction="col" gap="3px" grow class="sell-note__body">
      <Text
        v-if="title"
        is="span"
        type="caption"
        weight="semibold"
        :color="color"
        class="sell-note__title"
        >{{ title }}</Text
      >
      <Text is="span" type="caption" :color="color" class="sell-note__text"
        ><CurrencyText :text="text"
      /></Text>
    </Box>
  </Box>
</template>

<style scoped>
.sell-note {
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  box-sizing: border-box;
}

.sell-note--warning {
  background: var(--p-warning-50);
}

.sell-note--danger {
  background: var(--p-danger-50);
}

.sell-note--info {
  background: var(--p-info-50);
}

.sell-note--neutral {
  background: var(--p-sliceal-gray-100);
}

.sell-note__icon {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 14px;
}

.sell-note__body {
  min-width: 0;
}

.sell-note__title {
  line-height: 1.3;
}

.sell-note__text {
  line-height: 1.5;
}
</style>
